export interface Point {
  lat: number
  lng: number
}

export interface SnappedPoint extends Point {
  segmentIndex: number
  distanceFromLine: number
  progressOnSegment: number
}

export interface PolylineSegment {
  start: Point
  end: Point
  index: number
  bearing: number
  length: number
}

// Calculate distance between two points using Haversine formula
export function calculateDistance(p1: Point, p2: Point): number {
  const R = 6371000 // Earth's radius in meters
  const φ1 = (p1.lat * Math.PI) / 180
  const φ2 = (p2.lat * Math.PI) / 180
  const Δφ = ((p2.lat - p1.lat) * Math.PI) / 180
  const Δλ = ((p2.lng - p1.lng) * Math.PI) / 180

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return R * c
}

// Calculate bearing between two points
export function calculateBearing(p1: Point, p2: Point): number {
  const φ1 = (p1.lat * Math.PI) / 180
  const φ2 = (p2.lat * Math.PI) / 180
  const Δλ = ((p2.lng - p1.lng) * Math.PI) / 180

  const y = Math.sin(Δλ) * Math.cos(φ2)
  const x =
    Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ)

  const θ = Math.atan2(y, x)
  return ((θ * 180) / Math.PI + 360) % 360
}

// Calculate the closest point on a line segment to a given point
function getClosestPointOnSegment(
  point: Point,
  segmentStart: Point,
  segmentEnd: Point
): Point & { progress: number } {
  const dx = segmentEnd.lng - segmentStart.lng
  const dy = segmentEnd.lat - segmentStart.lat

  if (dx === 0 && dy === 0) {
    // Segment start and end are the same
    return { ...segmentStart, progress: 0 }
  }

  const t = Math.max(
    0,
    Math.min(
      1,
      ((point.lng - segmentStart.lng) * dx +
        (point.lat - segmentStart.lat) * dy) /
        (dx * dx + dy * dy)
    )
  )

  return {
    lat: segmentStart.lat + t * dy,
    lng: segmentStart.lng + t * dx,
    progress: t,
  }
}

// Find the closest point on the entire polyline to a given point
export function snapToPolyline(
  point: Point,
  polyline: Point[],
  toleranceMeters: number = 50
): SnappedPoint | null {
  if (!polyline || polyline.length < 2) {
    return null
  }

  let closestPoint: SnappedPoint | null = null
  let minDistance = Infinity

  for (let i = 0; i < polyline.length - 1; i++) {
    const segmentStart = polyline[i]
    const segmentEnd = polyline[i + 1]

    const projectedPoint = getClosestPointOnSegment(
      point,
      segmentStart,
      segmentEnd
    )
    const distance = calculateDistance(point, projectedPoint)

    if (distance < minDistance) {
      minDistance = distance
      closestPoint = {
        lat: projectedPoint.lat,
        lng: projectedPoint.lng,
        segmentIndex: i,
        distanceFromLine: distance,
        progressOnSegment: projectedPoint.progress,
      }
    }
  }

  // Only snap if within tolerance
  if (closestPoint && closestPoint.distanceFromLine <= toleranceMeters) {
    return closestPoint
  }

  return null
}

// Get polyline segments with metadata
export function getPolylineSegments(polyline: Point[]): PolylineSegment[] {
  const segments: PolylineSegment[] = []

  for (let i = 0; i < polyline.length - 1; i++) {
    const start = polyline[i]
    const end = polyline[i + 1]

    segments.push({
      start,
      end,
      index: i,
      bearing: calculateBearing(start, end),
      length: calculateDistance(start, end),
    })
  }

  return segments
}

// Find which segments need to be traversed between two snapped points
export function getSegmentsToTraverse(
  fromPoint: SnappedPoint,
  toPoint: SnappedPoint,
  segments: PolylineSegment[]
): PolylineSegment[] {
  const segmentsToTraverse: PolylineSegment[] = []

  if (fromPoint.segmentIndex === toPoint.segmentIndex) {
    // Moving within the same segment
    return [segments[fromPoint.segmentIndex]]
  }

  // Moving across multiple segments
  const startIndex = Math.min(fromPoint.segmentIndex, toPoint.segmentIndex)
  const endIndex = Math.max(fromPoint.segmentIndex, toPoint.segmentIndex)

  for (let i = startIndex; i <= endIndex; i++) {
    if (segments[i]) {
      segmentsToTraverse.push(segments[i])
    }
  }

  return segmentsToTraverse
}

// Interpolate position along a polyline segment
export function interpolateAlongSegment(
  segment: PolylineSegment,
  progress: number // 0 to 1
): Point {
  return {
    lat: segment.start.lat + (segment.end.lat - segment.start.lat) * progress,
    lng: segment.start.lng + (segment.end.lng - segment.start.lng) * progress,
  }
}

// Calculate total distance along polyline up to a specific point
export function getDistanceAlongPolyline(
  polyline: Point[],
  targetPoint: SnappedPoint
): number {
  let totalDistance = 0

  // Add full segments before the target segment
  for (let i = 0; i < targetPoint.segmentIndex; i++) {
    totalDistance += calculateDistance(polyline[i], polyline[i + 1])
  }

  // Add partial distance in the target segment
  if (targetPoint.segmentIndex < polyline.length - 1) {
    const segmentLength = calculateDistance(
      polyline[targetPoint.segmentIndex],
      polyline[targetPoint.segmentIndex + 1]
    )
    totalDistance += segmentLength * targetPoint.progressOnSegment
  }

  return totalDistance
}

// Check if a point is within a reasonable distance from the polyline
export function isPointNearPolyline(
  point: Point,
  polyline: Point[],
  maxDistanceMeters: number = 100
): boolean {
  const snappedPoint = snapToPolyline(point, polyline, maxDistanceMeters)
  return snappedPoint !== null
}

// Smooth GPS noise by averaging recent positions
export function smoothGPSNoise(
  currentPosition: Point,
  historicalPositions: Point[],
  maxHistorySize: number = 3
): Point {
  if (historicalPositions.length === 0) {
    return currentPosition
  }

  const positions = [
    ...historicalPositions.slice(-maxHistorySize),
    currentPosition,
  ]
  const avgLat = positions.reduce((sum, p) => sum + p.lat, 0) / positions.length
  const avgLng = positions.reduce((sum, p) => sum + p.lng, 0) / positions.length

  return { lat: avgLat, lng: avgLng }
}

// DYNAMIC POLYLINE UTILITIES FOR GOOGLE MAPS-STYLE ROUTE CUTTING

export interface TrimmedPolylineResult {
  remainingRoute: Point[]
  traveledRoute: Point[]
  totalDistance: number
  remainingDistance: number
  progressPercentage: number
}

// Trim polyline from current marker position to destination
export function trimPolylineFromPosition(
  fullRoute: Point[],
  currentPosition: Point,
  toleranceMeters: number = 50
): TrimmedPolylineResult {
  if (!fullRoute || fullRoute.length < 2) {
    return {
      remainingRoute: [],
      traveledRoute: [],
      totalDistance: 0,
      remainingDistance: 0,
      progressPercentage: 0,
    }
  }

  // Snap current position to the polyline
  const snappedPoint = snapToPolyline(
    currentPosition,
    fullRoute,
    toleranceMeters
  )

  if (!snappedPoint) {
    // If we can't snap to polyline, return the full route
    const totalDistance = calculateTotalPolylineDistance(fullRoute)
    return {
      remainingRoute: fullRoute,
      traveledRoute: [],
      totalDistance,
      remainingDistance: totalDistance,
      progressPercentage: 0,
    }
  }

  // Calculate traveled and remaining routes
  const traveledRoute = getTraveledRoute(fullRoute, snappedPoint)
  const remainingRoute = getRemainingRoute(fullRoute, snappedPoint)

  // Calculate distances
  const totalDistance = calculateTotalPolylineDistance(fullRoute)
  const traveledDistance = getDistanceAlongPolyline(fullRoute, snappedPoint)
  const remainingDistance = totalDistance - traveledDistance
  const progressPercentage =
    totalDistance > 0 ? (traveledDistance / totalDistance) * 100 : 0

  return {
    remainingRoute,
    traveledRoute,
    totalDistance,
    remainingDistance,
    progressPercentage,
  }
}

// Get the traveled portion of the route (from start to current position)
export function getTraveledRoute(
  fullRoute: Point[],
  snappedPoint: SnappedPoint
): Point[] {
  if (snappedPoint.segmentIndex === 0 && snappedPoint.progressOnSegment === 0) {
    // At the very beginning
    return [fullRoute[0]]
  }

  const traveledRoute: Point[] = []

  // Add all points before the current segment
  for (let i = 0; i <= snappedPoint.segmentIndex; i++) {
    traveledRoute.push(fullRoute[i])
  }

  // Add the snapped point if it's not at the start of the segment
  if (snappedPoint.progressOnSegment > 0) {
    traveledRoute.push({
      lat: snappedPoint.lat,
      lng: snappedPoint.lng,
    })
  }

  return traveledRoute
}

// Get the remaining portion of the route (from current position to end)
export function getRemainingRoute(
  fullRoute: Point[],
  snappedPoint: SnappedPoint
): Point[] {
  const remainingRoute: Point[] = []

  // Start with the snapped point (current marker position)
  remainingRoute.push({
    lat: snappedPoint.lat,
    lng: snappedPoint.lng,
  })

  // Add the end of current segment if we're not at the end
  if (
    snappedPoint.progressOnSegment < 1 &&
    snappedPoint.segmentIndex < fullRoute.length - 1
  ) {
    remainingRoute.push(fullRoute[snappedPoint.segmentIndex + 1])
  }

  // Add all remaining points
  for (let i = snappedPoint.segmentIndex + 2; i < fullRoute.length; i++) {
    remainingRoute.push(fullRoute[i])
  }

  return remainingRoute
}

// Calculate total distance of a polyline
export function calculateTotalPolylineDistance(polyline: Point[]): number {
  if (!polyline || polyline.length < 2) {
    return 0
  }

  let totalDistance = 0
  for (let i = 0; i < polyline.length - 1; i++) {
    totalDistance += calculateDistance(polyline[i], polyline[i + 1])
  }

  return totalDistance
}

// Create animated transition points between two polylines
export function createTransitionPoints(
  fromPolyline: Point[],
  toPolyline: Point[],
  progress: number // 0 to 1
): Point[] {
  if (progress <= 0) return fromPolyline
  if (progress >= 1) return toPolyline

  // Simple interpolation - for more complex animations, this can be enhanced
  const maxLength = Math.max(fromPolyline.length, toPolyline.length)
  const transitionPoints: Point[] = []

  for (let i = 0; i < maxLength; i++) {
    const fromPoint =
      fromPolyline[Math.min(i, fromPolyline.length - 1)] ||
      fromPolyline[fromPolyline.length - 1]
    const toPoint =
      toPolyline[Math.min(i, toPolyline.length - 1)] ||
      toPolyline[toPolyline.length - 1]

    transitionPoints.push({
      lat: fromPoint.lat + (toPoint.lat - fromPoint.lat) * progress,
      lng: fromPoint.lng + (toPoint.lng - fromPoint.lng) * progress,
    })
  }

  return transitionPoints
}

// Get polyline points at regular intervals for smooth animation
export function resamplePolyline(
  polyline: Point[],
  intervalMeters: number = 50
): Point[] {
  if (!polyline || polyline.length < 2) {
    return polyline
  }

  const resampledPoints: Point[] = [polyline[0]] // Always include start point
  let accumulatedDistance = 0
  let nextTargetDistance = intervalMeters

  for (let i = 0; i < polyline.length - 1; i++) {
    const segmentStart = polyline[i]
    const segmentEnd = polyline[i + 1]
    const segmentLength = calculateDistance(segmentStart, segmentEnd)

    // Check if we need to add points within this segment
    while (accumulatedDistance + segmentLength >= nextTargetDistance) {
      const distanceIntoSegment = nextTargetDistance - accumulatedDistance
      const progress = distanceIntoSegment / segmentLength

      const interpolatedPoint = {
        lat: segmentStart.lat + (segmentEnd.lat - segmentStart.lat) * progress,
        lng: segmentStart.lng + (segmentEnd.lng - segmentStart.lng) * progress,
      }

      resampledPoints.push(interpolatedPoint)
      nextTargetDistance += intervalMeters
    }

    accumulatedDistance += segmentLength
  }

  // Always include end point
  const lastPoint = polyline[polyline.length - 1]
  if (resampledPoints[resampledPoints.length - 1] !== lastPoint) {
    resampledPoints.push(lastPoint)
  }

  return resampledPoints
}

// INTELLIGENT ROUTE OPTIMIZATION - DEVIATION-BASED API REFETCHING

export interface RouteDeviationResult {
  isOnRoute: boolean
  distanceFromRoute: number
  deviationSeverity: "none" | "minor" | "moderate" | "major"
  shouldRefetchRoute: boolean
  confidenceScore: number // 0-100, higher = more confident in decision
  timeOffRoute?: number // milliseconds since first deviation
  consecutiveDeviations: number
}

export interface RouteOptimizationConfig {
  // Distance thresholds
  minDeviationDistance: number // meters - minimum distance to consider deviation
  moderateDeviationDistance: number // meters - threshold for moderate deviation
  majorDeviationDistance: number // meters - threshold for major deviation

  // Time thresholds
  minDeviationTime: number // milliseconds - time before considering refetch
  maxDeviationTime: number // milliseconds - force refetch after this time

  // Behavior settings
  consecutiveDeviationsThreshold: number // number of consecutive deviations before refetch
  confidenceThreshold: number // minimum confidence score to trigger refetch
  cooldownPeriod: number // milliseconds - minimum time between API calls

  // Context awareness
  considerSpeed: boolean // factor in vehicle speed for decisions
  considerBearing: boolean // factor in bearing changes
  urbanTolerance: number // higher tolerance in urban areas (multiplier)
}

export const DEFAULT_ROUTE_OPTIMIZATION_CONFIG: RouteOptimizationConfig = {
  minDeviationDistance: 50, // 50 meters
  moderateDeviationDistance: 100, // 100 meters
  majorDeviationDistance: 200, // 200 meters
  minDeviationTime: 15000, // 15 seconds
  maxDeviationTime: 60000, // 60 seconds
  consecutiveDeviationsThreshold: 3,
  confidenceThreshold: 70,
  cooldownPeriod: 30000, // 30 seconds
  considerSpeed: true,
  considerBearing: true,
  urbanTolerance: 1.5,
}

// Track route deviation over time
export class RouteDeviationTracker {
  private deviationHistory: Array<{
    timestamp: number
    distanceFromRoute: number
    position: Point
    isOnRoute: boolean
  }> = []

  private firstDeviationTime: number | null = null
  private lastRefetchTime: number = 0
  private consecutiveDeviations = 0

  constructor(
    private config: RouteOptimizationConfig = DEFAULT_ROUTE_OPTIMIZATION_CONFIG
  ) {}

  analyzeDeviation(
    currentPosition: Point,
    currentRoute: Point[],
    currentSpeed?: number, // km/h
    currentBearing?: number
  ): RouteDeviationResult {
    const now = Date.now()

    // Check if we're within cooldown period
    if (now - this.lastRefetchTime < this.config.cooldownPeriod) {
      return {
        isOnRoute: true, // Force "on route" during cooldown
        distanceFromRoute: 0,
        deviationSeverity: "none",
        shouldRefetchRoute: false,
        confidenceScore: 100,
        consecutiveDeviations: this.consecutiveDeviations,
      }
    }

    // Snap position to route
    const snappedPoint = snapToPolyline(
      currentPosition,
      currentRoute,
      this.config.majorDeviationDistance
    )
    const distanceFromRoute = snappedPoint?.distanceFromLine || Infinity
    const isOnRoute =
      snappedPoint !== null &&
      distanceFromRoute <= this.config.minDeviationDistance

    // Update deviation history
    this.deviationHistory.push({
      timestamp: now,
      distanceFromRoute,
      position: currentPosition,
      isOnRoute,
    })

    // Keep only recent history (last 2 minutes)
    const cutoffTime = now - 120000
    this.deviationHistory = this.deviationHistory.filter(
      (h) => h.timestamp > cutoffTime
    )

    // Track consecutive deviations
    if (!isOnRoute) {
      this.consecutiveDeviations++
      if (this.firstDeviationTime === null) {
        this.firstDeviationTime = now
      }
    } else {
      this.consecutiveDeviations = 0
      this.firstDeviationTime = null
    }

    // Determine deviation severity
    let deviationSeverity: RouteDeviationResult["deviationSeverity"] = "none"
    if (distanceFromRoute > this.config.majorDeviationDistance) {
      deviationSeverity = "major"
    } else if (distanceFromRoute > this.config.moderateDeviationDistance) {
      deviationSeverity = "moderate"
    } else if (distanceFromRoute > this.config.minDeviationDistance) {
      deviationSeverity = "minor"
    }

    // Calculate confidence score
    const confidenceScore = this.calculateConfidenceScore(
      distanceFromRoute,
      currentSpeed,
      currentBearing,
      currentRoute,
      snappedPoint
    )

    // Determine if we should refetch
    const shouldRefetchRoute = this.shouldRefetchRoute(
      deviationSeverity,
      confidenceScore,
      now
    )

    if (shouldRefetchRoute) {
      this.lastRefetchTime = now
      this.consecutiveDeviations = 0
      this.firstDeviationTime = null
    }

    return {
      isOnRoute,
      distanceFromRoute,
      deviationSeverity,
      shouldRefetchRoute,
      confidenceScore,
      timeOffRoute: this.firstDeviationTime
        ? now - this.firstDeviationTime
        : undefined,
      consecutiveDeviations: this.consecutiveDeviations,
    }
  }

  private calculateConfidenceScore(
    distanceFromRoute: number,
    speed?: number,
    bearing?: number,
    route?: Point[],
    snappedPoint?: SnappedPoint | null
  ): number {
    let confidence = 50 // Base confidence

    // Distance factor (higher distance = higher confidence in deviation)
    if (distanceFromRoute > this.config.majorDeviationDistance) {
      confidence += 30
    } else if (distanceFromRoute > this.config.moderateDeviationDistance) {
      confidence += 20
    } else if (distanceFromRoute > this.config.minDeviationDistance) {
      confidence += 10
    }

    // Speed factor
    if (this.config.considerSpeed && speed !== undefined) {
      if (speed > 30) {
        // High speed increases confidence
        confidence += 15
      } else if (speed < 5) {
        // Very low speed decreases confidence (might be stuck in traffic)
        confidence -= 10
      }
    }

    // Bearing consistency
    if (
      this.config.considerBearing &&
      bearing !== undefined &&
      snappedPoint &&
      route
    ) {
      const routeBearing = this.getRouteBearing(route, snappedPoint)
      if (routeBearing !== null) {
        const bearingDifference = Math.abs(bearing - routeBearing)
        const normalizedDifference = Math.min(
          bearingDifference,
          360 - bearingDifference
        )

        if (normalizedDifference > 90) {
          // Driving in wrong direction
          confidence += 25
        } else if (normalizedDifference < 30) {
          // Following route direction
          confidence -= 15
        }
      }
    }

    // Historical consistency
    const recentDeviations = this.deviationHistory.slice(-5)
    const deviationRate =
      recentDeviations.filter((h) => !h.isOnRoute).length /
      recentDeviations.length
    confidence += deviationRate * 20

    return Math.max(0, Math.min(100, confidence))
  }

  private getRouteBearing(
    route: Point[],
    snappedPoint: SnappedPoint
  ): number | null {
    if (snappedPoint.segmentIndex >= route.length - 1) return null

    const start = route[snappedPoint.segmentIndex]
    const end = route[snappedPoint.segmentIndex + 1]

    return calculateBearing(start, end)
  }

  private shouldRefetchRoute(
    severity: RouteDeviationResult["deviationSeverity"],
    confidence: number,
    currentTime: number
  ): boolean {
    // Never refetch if no deviation
    if (severity === "none") return false

    // Always refetch for major deviations with high confidence
    if (severity === "major" && confidence >= this.config.confidenceThreshold) {
      return true
    }

    // Refetch after consecutive moderate deviations
    if (
      severity === "moderate" &&
      this.consecutiveDeviations >=
        this.config.consecutiveDeviationsThreshold &&
      confidence >= this.config.confidenceThreshold - 20
    ) {
      return true
    }

    // Force refetch after maximum deviation time
    if (
      this.firstDeviationTime &&
      currentTime - this.firstDeviationTime > this.config.maxDeviationTime
    ) {
      return true
    }

    // Refetch after minimum time with sufficient confidence
    if (
      this.firstDeviationTime &&
      currentTime - this.firstDeviationTime > this.config.minDeviationTime &&
      confidence >= this.config.confidenceThreshold &&
      this.consecutiveDeviations >= 2
    ) {
      return true
    }

    return false
  }

  // Reset tracker (call when route is successfully refetched)
  reset() {
    this.deviationHistory = []
    this.firstDeviationTime = null
    this.consecutiveDeviations = 0
  }

  // Get debug information
  getDebugInfo() {
    return {
      historyLength: this.deviationHistory.length,
      consecutiveDeviations: this.consecutiveDeviations,
      timeOffRoute: this.firstDeviationTime
        ? Date.now() - this.firstDeviationTime
        : 0,
      lastRefetchTime: this.lastRefetchTime,
      config: this.config,
    }
  }
}
